import React, { useEffect, useRef, useState } from 'react';

const LOGO_SRC = process.env.PUBLIC_URL + '/logo.png';

// Unique rotating messages — school-specific, change every ~2s
const MESSAGES = [
  'Nurturing Minds, Building Futures',
  'Where Knowledge Meets Character',
  'Empowering Every Student',
  'Excellence in Every Classroom',
  'Shaping Tomorrow\'s Leaders',
  'Learning Beyond Boundaries',
  'Inspiring Curiosity, Igniting Potential',
  'A Legacy of Academic Excellence',
];

function AppLoader({ label = '' }) {
  const [progress, setProgress]     = useState(0);
  const [msgIdx, setMsgIdx]         = useState(0);
  const [msgVisible, setMsgVisible] = useState(true);
  const [done, setDone]             = useState(false);
  const canvasRef                   = useRef(null);
  const rafRef                      = useRef(null);

  /* ── Particle canvas ─────────────────────────────────────────── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W = canvas.width  = window.innerWidth;
    let H = canvas.height = window.innerHeight;

    const onResize = () => {
      W = canvas.width  = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', onResize);

    const particles = Array.from({ length: 30 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      r: Math.random() * 1.5 + 0.5,
      vy: -(Math.random() * 0.4 + 0.15),
      o: Math.random() * 0.07 + 0.02,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 10, 30, ${p.o})`;
        ctx.fill();
        p.y += p.vy;
        if (p.y < -10) { p.y = H + 10; p.x = Math.random() * W; }
      });
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  /* ── Rotating messages every 2s ─────────────────────────────── */
  useEffect(() => {
    if (done) return;
    const interval = setInterval(() => {
      setMsgVisible(false);
      setTimeout(() => {
        setMsgIdx(i => (i + 1) % MESSAGES.length);
        setMsgVisible(true);
      }, 350);
    }, 2200);
    return () => clearInterval(interval);
  }, [done]);

  /* ── Progress ticker ─────────────────────────────────────────── */
  useEffect(() => {
    let current = 0;
    let timeout;

    const tick = () => {
      if (current >= 100) {
        setProgress(100);
        setMsgVisible(false);
        setTimeout(() => {
          setMsgVisible(true);
          setDone(true);
        }, 400);
        return;
      }
      const inc = Math.random() * 12 + 2;
      current = Math.min(current + inc, 100);
      setProgress(current);
      timeout = setTimeout(tick, Math.random() * 1000 + 400);
    };

    const start = setTimeout(tick, 1600);
    return () => { clearTimeout(start); clearTimeout(timeout); };
  }, []);

  const displayMsg = done ? 'Welcome' : MESSAGES[msgIdx];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: '#fcf9f8',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden', userSelect: 'none',
      fontFamily: "'Libre Franklin', 'Inter', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@600;700&family=Libre+Franklin:wght@400;500;600&display=swap');

        @keyframes lkps-breathe {
          0%,100% { opacity: 0.06; transform: translate(-50%,-50%) scale(1); }
          50%      { opacity: 0.10; transform: translate(-50%,-50%) scale(1.04); }
        }
        @keyframes lkps-fade-up {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes lkps-shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
        @keyframes lkps-pulse-dot {
          0%,100% { opacity: 0.3; }
          50%     { opacity: 0.9; }
        }
        .lkps-fade-up-1 { animation: lkps-fade-up 1.2s cubic-bezier(0.2,0.8,0.2,1) 0.2s both; }
        .lkps-fade-up-2 { animation: lkps-fade-up 1.2s cubic-bezier(0.2,0.8,0.2,1) 0.4s both; }
        .lkps-fade-up-3 { animation: lkps-fade-up 1.2s cubic-bezier(0.2,0.8,0.2,1) 0.8s both; }
      `}</style>

      {/* Particle canvas */}
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />

      {/* ── Background watermark: school logo ── */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        animation: 'lkps-breathe 8s ease-in-out infinite',
        pointerEvents: 'none',
        width: 420, height: 420,
      }}>
        <img
          src={LOGO_SRC}
          alt=""
          style={{
            width: '100%', height: '100%',
            objectFit: 'contain',
            filter: 'grayscale(100%)',
            opacity: 1, // controlled by parent animation
          }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      </div>

      {/* ── Central content ── */}
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '0 24px' }}>

        {/* School name */}
        <div className="lkps-fade-up-1">
          <h1 style={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            fontSize: 'clamp(26px, 4.5vw, 52px)',
            fontWeight: 700,
            color: '#000a1e',
            letterSpacing: '-0.02em',
            lineHeight: 1.15,
            marginBottom: 6,
          }}>
            Lord Krishna Public School
          </h1>

          {/* Location */}
          <p style={{
            fontFamily: "'Libre Franklin', sans-serif",
            fontSize: 11,
            fontWeight: 600,
            color: '#735c00',
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            marginBottom: 0,
          }}>
            Mathura, Uttar Pradesh
          </p>

          {/* Gold divider */}
          <div style={{
            width: 96, height: 1,
            background: '#e9c349',
            margin: '18px auto 36px',
            opacity: 0.7,
          }} />
        </div>

        {/* Progress section */}
        <div className="lkps-fade-up-2" style={{ width: 260, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

          {/* Progress bar */}
          <div style={{
            position: 'relative', width: '100%', height: 2,
            background: 'rgba(0,10,30,0.1)',
            borderRadius: 99, overflow: 'hidden',
            maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
            WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
          }}>
            <div style={{
              position: 'absolute', top: 0, left: 0,
              height: '100%', borderRadius: 99,
              background: done ? '#735c00' : '#000a1e',
              width: `${progress}%`,
              transition: 'width 0.7s cubic-bezier(0.4,0,0.2,1), background 0.5s ease',
            }} />
            <div style={{
              position: 'absolute', top: 0, left: 0,
              height: '100%', width: '33%',
              background: 'linear-gradient(to right, transparent, rgba(233,195,73,0.5), transparent)',
              animation: 'lkps-shimmer 2s ease-in-out infinite',
            }} />
          </div>

          {/* Rotating message */}
          <div style={{ marginTop: 28, minHeight: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <p style={{
              fontFamily: "'Libre Franklin', sans-serif",
              fontSize: done ? 11 : 12,
              fontWeight: done ? 700 : 500,
              color: done ? '#000a1e' : 'rgba(0,10,30,0.55)',
              letterSpacing: done ? '0.35em' : '0.05em',
              textTransform: done ? 'uppercase' : 'none',
              textAlign: 'center',
              maxWidth: 280,
              opacity: msgVisible ? 1 : 0,
              transform: msgVisible ? 'translateY(0)' : 'translateY(6px)',
              transition: 'opacity 0.35s ease, transform 0.35s ease, letter-spacing 0.5s ease, color 0.5s ease',
            }}>
              {displayMsg}
            </p>

            {/* Pulse dot — hidden when done */}
            {!done && (
              <div style={{
                width: 4, height: 4, borderRadius: '50%',
                background: '#000a1e', opacity: 0.35,
                animation: 'lkps-pulse-dot 1.5s ease-in-out infinite',
              }} />
            )}
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="lkps-fade-up-3" style={{
        position: 'absolute', bottom: 44, width: '100%',
        textAlign: 'center', display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: 10,
      }}>
        <div style={{ width: 24, height: 1, background: 'rgba(0,10,30,0.18)' }} />
        <p style={{
          fontFamily: "'Libre Franklin', sans-serif",
          fontSize: 9,
          fontWeight: 500,
          color: 'rgba(0,10,30,0.32)',
          letterSpacing: '0.25em',
          textTransform: 'uppercase',
        }}>
          Excellence in Education · Est. 1995
        </p>
      </footer>
    </div>
  );
}

export default AppLoader;
